import type { PortalRole } from '../types/portalUser';

export const ALL_PORTAL_ROLES: PortalRole[] = ['admin', 'commercial', 'coach', 'support'];
export const ADMIN_ONLY_ROLES: PortalRole[] = ['admin'];
export const ADMINISTRATION_ROLES: PortalRole[] = ['admin', 'support'];
export const STUDENT_VIEW_ROLES: PortalRole[] = ['admin', 'commercial', 'coach', 'support'];
export const COMMERCIAL_ROLES: PortalRole[] = ['admin', 'commercial'];
export const ONBOARDING_ROLES: PortalRole[] = ['admin', 'commercial', 'support'];
export const COMMERCIAL_DASHBOARD_ROLES: PortalRole[] = ['admin'];
export const COACH_ROLES: PortalRole[] = ['admin', 'coach'];
export const ORCHESTRATOR_ROLES: PortalRole[] = ['admin', 'support', 'coach'];
export const DASHBOARD_ROLES: PortalRole[] = ['admin', 'commercial', 'support'];
export const PERFORMANCE_SUPPORT_DASHBOARD_ROLES: PortalRole[] = ['admin', 'support', 'coach'];
export const RECENT_BODY_PHOTOS_DASHBOARD_ROLES: PortalRole[] = ['admin', 'support', 'coach'];
export const SENSITIVE_STUDENTS_DASHBOARD_ROLES: PortalRole[] = ['admin', 'commercial', 'support', 'coach'];

const hasAnyRole = (roles: PortalRole[], allowedRoles: PortalRole[]): boolean =>
  roles.some((role) => allowedRoles.includes(role));

const hasRole = (roles: PortalRole[], role: PortalRole): boolean => roles.includes(role);

export const getDefaultPathForRoles = (roles: PortalRole[]): string => {
  if (hasRole(roles, 'admin')) return '/';
  if (hasRole(roles, 'commercial')) return '/comercial';
  if (hasRole(roles, 'coach')) return '/alunas';
  if (hasRole(roles, 'support')) return '/orquestrador';
  return '/login';
};

export const canAccessDashboard = (roles: PortalRole[]): boolean => hasAnyRole(roles, DASHBOARD_ROLES);
export const canAccessPerformanceSupportDashboard = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, PERFORMANCE_SUPPORT_DASHBOARD_ROLES);
export const canAccessRecentBodyPhotosDashboard = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, RECENT_BODY_PHOTOS_DASHBOARD_ROLES);
export const canAccessSensitiveStudentsDashboard = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, SENSITIVE_STUDENTS_DASHBOARD_ROLES);
export const canAccessStudents = (roles: PortalRole[]): boolean => hasAnyRole(roles, STUDENT_VIEW_ROLES);
export const canManageStudents = (roles: PortalRole[]): boolean => hasRole(roles, 'admin');
export const canInactivateStudents = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, ['admin', 'support']);
export const canAccessPayments = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, ['admin', 'support']);
/** Criar/cancelar assinatura, vincular conta e demais mutações de cobrança (payment-gateway). */
export const canManageStudentSubscriptions = (roles: PortalRole[]): boolean =>
  canAccessPayments(roles);
export const canViewStudentPayments = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, ['admin', 'support', 'commercial']);
export const canAccessPerformance = (roles: PortalRole[]): boolean => hasAnyRole(roles, COACH_ROLES);
export const canChangeStudentCoach = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, ['admin', 'support', 'coach']);
export const canAccessTrainings = (roles: PortalRole[]): boolean => hasAnyRole(roles, COACH_ROLES);
export const canManageTrainings = (roles: PortalRole[]): boolean => hasAnyRole(roles, COACH_ROLES);
export const canDeliverWorkoutInPerformance = (roles: PortalRole[]): boolean => hasAnyRole(roles, COACH_ROLES);
export const canInsertStudentWorkoutHistory = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, ['admin', 'support', 'coach']);
export const canPauseSubscription = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, ['admin', 'support']);
export const canAccessExercises = (roles: PortalRole[]): boolean => hasAnyRole(roles, COACH_ROLES);
export const canManageExercises = (roles: PortalRole[]): boolean => hasAnyRole(roles, COACH_ROLES);
export const canAccessCommercial = (roles: PortalRole[]): boolean => hasAnyRole(roles, COMMERCIAL_ROLES);
export const canAccessCommercialDashboard = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, COMMERCIAL_DASHBOARD_ROLES);
export const canAccessOnboarding = (roles: PortalRole[]): boolean => hasAnyRole(roles, ONBOARDING_ROLES);
export const canAccessOrchestrator = (roles: PortalRole[]): boolean => hasAnyRole(roles, ORCHESTRATOR_ROLES);
export const canAccessAdministration = (roles: PortalRole[]): boolean => hasAnyRole(roles, ADMINISTRATION_ROLES);
export const canAccessFullAdministration = (roles: PortalRole[]): boolean => hasRole(roles, 'admin');
export const canManageSubscriptionPlans = (roles: PortalRole[]): boolean =>
  hasRole(roles, 'admin');
export const canEditExistingStudent = (roles: PortalRole[]): boolean =>
  hasRole(roles, 'admin') || hasRole(roles, 'commercial');
export const canEditStudentPersonalInfo = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, ['admin', 'commercial', 'support']);
export const canAssignOtherCommercialOwners = (roles: PortalRole[]): boolean =>
  hasRole(roles, 'admin');
export const canConfigureSalesGoals = (roles: PortalRole[]): boolean =>
  hasRole(roles, 'admin');
/** Internal CS tab. Widen ADMIN_ONLY_ROLES usage here when more roles should see it. */
export const canViewStudentBehavioralProfile = (roles: PortalRole[]): boolean =>
  hasAnyRole(roles, ADMIN_ONLY_ROLES);

export const hasRequiredRole = (roles: PortalRole[], allowedRoles?: PortalRole[]): boolean => {
  if (!allowedRoles || allowedRoles.length === 0) {
    return true;
  }

  return hasAnyRole(roles, allowedRoles);
};

const isPortalRole = (value: unknown): value is PortalRole =>
  typeof value === 'string' && ALL_PORTAL_ROLES.includes(value as PortalRole);

const extractPortalRole = (item: unknown): PortalRole | null => {
  if (isPortalRole(item)) {
    return item;
  }

  if (item && typeof item === 'object' && 'role' in item) {
    const role = (item as { role: unknown }).role;
    return isPortalRole(role) ? role : null;
  }

  return null;
};

export const normalizePortalRoles = (value: unknown): PortalRole[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const roles = value
    .map(extractPortalRole)
    .filter((role): role is PortalRole => role !== null);

  return roles.filter((role, index) => roles.indexOf(role) === index);
};

export const resolvePortalUserRoles = (source: {
  roles?: unknown;
  role?: unknown;
}): PortalRole[] => {
  const fromRoles = normalizePortalRoles(source.roles);
  if (fromRoles.length > 0) {
    return fromRoles;
  }

  return isPortalRole(source.role) ? [source.role] : [];
};
